import React from "react";
import { Tooltip } from "antd";
import {
  FsFn,
  TreeRow,
  fmtINR,
  fmtINRCompact,
  fmtINRSigned,
  fmtQty,
  num,
  perUnit,
} from "./threeMShared";

// Shared money cell for the 3M trees: compact value with the exact amount on
// hover, and a muted ₹0 for zero so empty cells recede instead of shouting.
export const MUTED = "#8c94a8";

export const compactAmountCell = (value: number, bold = false) =>
  value > 0 ? (
    <Tooltip title={fmtINR(value)}>
      <span
        style={{
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          fontWeight: bold ? 600 : undefined,
        }}
      >
        {fmtINRCompact(value)}
      </span>
    </Tooltip>
  ) : (
    <span
      style={{
        color: MUTED,
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
    >
      ₹0
    </span>
  );

// Per-unit sub-line: amount ÷ the row's work quantity, shown in its work UOM
// ("₹1,627.40/Cum") — the rate a site actually manages against. Task rows use
// the task's own work qty; phase/activity rows use the SUM of their tasks'
// quantities, which buildTree/aggWorkBasis sets ONLY when the child UOMs are
// uniform, so mixed-UOM parents have no quantity and correctly show no rate.
// `signed` is for deviation columns, where the value carries its own +/− and a
// zero means "no variance" rather than "nothing to show".
export const perUnitLine = (
  basis: { workQuantity?: string; workUom?: string },
  amount: number,
  fs: FsFn,
  signed = false
) => {
  const rate = perUnit(amount, num(basis?.workQuantity));
  if (rate === null || (signed ? amount === 0 : !(amount > 0))) return null;
  return (
    <div
      style={{
        fontSize: fs(11),
        color: "#8094ae",
        fontVariantNumeric: "tabular-nums",
        whiteSpace: "nowrap",
      }}
    >
      {signed ? fmtINRSigned(rate, true) : fmtINR(rate)}
      {basis?.workUom ? `/${basis.workUom}` : "/unit"}
    </div>
  );
};

// The BOQ line under a row's name: "2,020 Cubic Meter @ ₹1,841.57 = ₹37.20 L"
// — work quantity, the task's Basic Rate and the resulting value. This is the
// PRICE side of the row; every ₹ column on these trees is 3M COST.
//
// The rate is the Basic Rate: the client's contract rate net of GST (a
// pass-through) and BOCW labour cess (withheld by the client). That puts the
// price line on the same tax basis as the 3M cost columns beside it.
//
// Quantity follows the tree's UOM rule (parents only when their tasks share a
// UOM), but the VALUE always shows: rupees sum across UOMs even where
// quantities cannot, so a mixed-UOM phase still reports its BOQ worth. A
// parent's rate is therefore its value ÷ quantity — a quantity-weighted
// average — and only appears when that quantity exists.
export const boqBasisLine = (
  row: Pick<TreeRow, "workQuantity" | "workUom" | "rate" | "boqValue">,
  fs: FsFn,
  opts: { prefix?: string; showUom?: boolean } = {}
) => {
  const { prefix = "", showUom = true } = opts;
  const qty = num(row?.workQuantity);
  const value = row?.boqValue ?? qty * num(row?.rate);
  if (!(qty > 0) && !(value > 0)) return null;
  const rate = row?.rate ? num(row.rate) : perUnit(value, qty);
  const parts: string[] = [];
  if (qty > 0) {
    parts.push(
      `${prefix}${fmtQty(qty)}${showUom && row?.workUom ? ` ${row.workUom}` : ""}`
    );
  }
  if (qty > 0 && rate !== null && rate > 0) parts.push(`@ ${fmtINR(rate)}`);
  // "qty @ rate = value" reads as a price line on its own; a bare value (the
  // mixed-UOM parent case) would read as one more cost column, so label it.
  if (value > 0) {
    parts.push(
      parts.length ? `= ${fmtINRCompact(value)}` : `BOQ ${fmtINRCompact(value)}`
    );
  }
  return (
    <Tooltip
      title={
        <div style={{ fontSize: fs(12), lineHeight: 1.7 }}>
          <div style={{ fontWeight: 600, marginBottom: 2 }}>BOQ (price)</div>
          {qty > 0 ? (
            <div>
              Quantity: {fmtQty(qty)} {row?.workUom || ""}
            </div>
          ) : (
            <div style={{ opacity: 0.75 }}>
              Tasks here use different UOMs — no single quantity.
            </div>
          )}
          {rate !== null && rate > 0 ? (
            <div>
              Basic Rate: {fmtINR(rate)}
              {row?.workUom ? `/${row.workUom}` : ""}
              {row?.rate ? "" : " (qty-weighted average)"}
            </div>
          ) : null}
          {/* Never print "Value: ₹0.00" — a row with no rate has an UNKNOWN
              BOQ value, and a printed zero reads as a priced-at-nothing line. */}
          {value > 0 ? (
            <div>Value: {fmtINR(value)}</div>
          ) : (
            <div style={{ opacity: 0.75 }}>No BOQ rate on this task.</div>
          )}
          <div style={{ marginTop: 4, opacity: 0.85 }}>
            The ₹ columns are 3M cost, not price.
          </div>
        </div>
      }
    >
      <span
        style={{
          fontSize: fs(11),
          color: "#8c8c8c",
          fontVariantNumeric: "tabular-nums",
          cursor: "help",
        }}
      >
        {parts.join(" ")}
      </span>
    </Tooltip>
  );
};
